/**
 * Shared "print a PDF" helpers.
 *
 * A workstation can designate a **Documents** printer (Settings → Devices) and
 * the receipts pages let staff pick a target per page via a `?printer=` URL
 * param. When a printer is selected and the local print agent is running, the
 * PDF is sent straight to that printer — no browser dialog. Otherwise it opens
 * in a new tab for the normal browser print dialog.
 */
import { toast } from 'sonner';
import {
  ConnectedDevice,
  getDeviceHost,
  getDocumentPrinter,
  getDiagnosticReceiptPrinter,
  getInvoicePrinter,
  isRemoteHost,
} from './devices';
import {
  agentPrintDocument,
  blobToBase64,
  getPrintAgentIdentities,
  isPrintAgentAvailable,
} from './printAgent';
import { enqueueDocumentBase64 } from './printJobs';
import { emitPrintRequest } from './printSocket';
import { notifyPrintAgentOffline } from './printAgentOffline';

export type PrintOutcome = 'agent' | 'dialog' | 'error';

/**
 * Print an already-fetched PDF object URL to a specific device (or the browser
 * dialog when `device` is null). Reports failures through `onError`.
 */
export async function printPdfToDevice(
  url: string,
  device: ConnectedDevice | null,
  opts?: { onError?: (message: string) => void },
): Promise<PrintOutcome> {
  if (!device) {
    window.open(url, '_blank');
    return 'dialog';
  }

  const host = getDeviceHost(device);
  const identity = await getPrintAgentIdentities(true);
  const remote = Boolean(
    device.shareName && isRemoteHost(host, identity.hostname, identity.ipAddresses),
  );

  const readBase64 = async (): Promise<string> => {
    const blob = await (await fetch(url)).blob();
    return blobToBase64(blob);
  };

  // A printer on another PC is sent straight to its Windows share over SMB from
  // THIS PC's agent, so the printer's PC needs no agent or dashboard. If that
  // fails, fall back to the cloud relay (which needs an agent on that PC).
  if (remote) {
    let directError: any = null;
    let base64: string | null;
    try {
      base64 = await readBase64();
    } catch {
      base64 = null;
    }

    // 1. A printer on another PC is sent straight to its Windows share over SMB
    // from THIS PC's agent, so the printer's PC needs no agent or dashboard.
    if (base64 && (await isPrintAgentAvailable(1200, true))) {
      try {
        await agentPrintDocument(base64, 'pdf', device.shareName, host);
        URL.revokeObjectURL(url);
        toast.success(`Sent to ${device.name || device.shareName} on ${host}`);
        return 'agent';
      } catch (err) {
        directError = err;
      }
    }

    // 2. Real-time socket relay: the backend wakes the agent on the printer's PC.
    if (base64) {
      const relay = await emitPrintRequest({
        shareName: device.shareName,
        host,
        kind: 'document',
        format: 'pdf',
        printerLabel: device.name,
        source: 'document',
        payload: base64,
      });
      if (relay?.ok) {
        URL.revokeObjectURL(url);
        if (relay.delivered === false) {
          notifyPrintAgentOffline({
            host,
            shareName: device.shareName || undefined,
            printerName: device.name || device.shareName,
          });
          return 'error';
        }
        toast.success(`Sent to ${device.name || device.shareName} on ${host}`);
        return 'agent';
      }
    }

    // 3. Legacy REST relay: enqueues the job (the backend resolves the owning
    //    agent by share, else falls back to a dashboard on the printer's PC).
    try {
      const data = base64 ?? (await readBase64());
      const res = await enqueueDocumentBase64(data, device.shareName, host, device.name, 'document');
      URL.revokeObjectURL(url);
      if (res.data?.delivered === false) {
        notifyPrintAgentOffline({
          host,
          shareName: device.shareName || undefined,
          printerName: device.name || device.shareName,
        });
        return 'error';
      }
      toast.success(`Sent to ${device.name || device.shareName} on ${host}`);
      return 'agent';
    } catch (relayErr: any) {
      URL.revokeObjectURL(url);
      const message =
        directError?.message || relayErr?.message || 'Failed to print';
      toast.error(message);
      opts?.onError?.(message);
      return 'error';
    }
  }

  if (!(await isPrintAgentAvailable(1200, true))) {
    // No agent on this PC: queue the job for the dashboard on the printer's own
    // PC so it still prints without a local agent.
    if (device.shareName) {
      try {
        const base64 = await readBase64();
        const res = await enqueueDocumentBase64(base64, device.shareName, host, device.name, 'document');
        URL.revokeObjectURL(url);
        if (res.data?.delivered === false) {
          notifyPrintAgentOffline({
            host,
            shareName: device.shareName || undefined,
            printerName: device.name || device.shareName,
          });
          return 'error';
        }
        toast.success(`Sent to ${device.name || device.shareName}`);
        return 'agent';
      } catch {
        /* fall through to the browser dialog */
      }
    }
    // Truly no way to deliver — fall back to the browser dialog so it still prints.
    window.open(url, '_blank');
    toast.warning('No print agent is running on this PC — opened the browser print dialog.');
    return 'dialog';
  }

  try {
    const base64 = await readBase64();
    await agentPrintDocument(base64, 'pdf', device.shareName, undefined);
    URL.revokeObjectURL(url);
    toast.success(`Sent to ${device.name || device.shareName}`);
    return 'agent';
  } catch (err: any) {
    const message = err?.message || 'Failed to print';
    toast.error(message);
    opts?.onError?.(message);
    return 'error';
  }
}

/**
 * Convenience wrapper for the document prints that are not tied to a page-level
 * target: uses the designated Documents printer, else the browser dialog.
 */
export async function printPdfBlobUrl(
  url: string,
  opts?: { onError?: (message: string) => void },
): Promise<PrintOutcome> {
  return printPdfToDevice(url, getDocumentPrinter() ?? null, opts);
}

/**
 * Print a diagnostic (lab test) receipt: uses the designated Diagnostic-receipts
 * printer, then the Documents printer, then the browser dialog.
 */
export async function printDiagnosticReceiptPdf(
  url: string,
  opts?: { onError?: (message: string) => void },
): Promise<PrintOutcome> {
  const device = getDiagnosticReceiptPrinter() ?? getDocumentPrinter() ?? null;
  return printPdfToDevice(url, device, opts);
}

/**
 * Print an invoice: uses the designated Invoices printer, then the Documents
 * printer, then the browser dialog.
 */
export async function printInvoicePdf(
  url: string,
  opts?: { onError?: (message: string) => void },
): Promise<PrintOutcome> {
  const device = getInvoicePrinter() ?? getDocumentPrinter() ?? null;
  return printPdfToDevice(url, device, opts);
}
